import { api } from './api'
import { API_ENDPOINTS } from '../constants/api'
import { TimelineEvent } from '../types/timeline'

export interface ClientCase {
  id: number
  case_number: string
  title: string
  description: string
  status: string
  priority: string
  opened_at: string
  closed_at?: string
  responsible_lawyer?: {
    id: number
    first_name: string
    last_name: string
    email: string
  }
  timeline_events: number
  client_documents: number
  upcoming_deadlines?: ClientDeadline[]
}

export interface ClientDeadline {
  id: number
  title: string
  description: string
  due_date: string
  status: string // upcoming, overdue, completed
  days_left: number
}

export interface ClientDocument {
  id: number
  uuid: string
  filename: string
  description: string
  file_size: number
  mime_type: string
  version: number
  uploaded_by: {
    id: number
    first_name: string
    last_name: string
    email: string
  }
  uploaded_at: string
  is_signed: boolean
  download_url?: string
}

export interface ClientStats {
  active_cases: number
  shared_documents: number
  upcoming_deadlines: number
  unread_notifications: number
}

export interface ClientCasesResponse {
  cases: ClientCase[]
  total: number
  page: number
  limit: number
}

export interface ClientDeadlinesResponse {
  deadlines: ClientDeadline[]
}

export interface ClientDocumentsResponse {
  documents: ClientDocument[]
}

export const clientService = {
  // Get client dashboard statistics
  async getStats(): Promise<ClientStats> {
    // Note: backend doesn't have a dedicated stats endpoint yet
    // We can compute from cases, documents, deadlines, notifications
    // For now, we'll compute client-side by fetching counts
    const cases = await this.getCases({ limit: 1, offset: 0 })
    const deadlines = await this.getDeadlines({ upcomingOnly: true })
    const documents = await this.getDocuments({ caseId: undefined, limit: 1 })
    // Notifications count will be fetched separately
    return {
      active_cases: cases.total,
      shared_documents: documents.documents.length,
      upcoming_deadlines: deadlines.deadlines.length,
      unread_notifications: 0, // placeholder
    }
  },

  // Get client cases with optional filters
  async getCases(params?: {
    status?: string
    limit?: number
    offset?: number
  }): Promise<ClientCasesResponse> {
    const query = new URLSearchParams()
    if (params?.status) query.append('status', params.status)
    if (params?.limit) query.append('limit', params.limit.toString())
    if (params?.offset) query.append('offset', params.offset.toString())
    const queryString = query.toString() ? `?${query.toString()}` : ''
    return api.get<ClientCasesResponse>(`${API_ENDPOINTS.CLIENT.CASES.BASE}${queryString}`)
  },

  // Get a specific case by ID
  async getCase(caseId: number): Promise<ClientCase> {
    return api.get<ClientCase>(API_ENDPOINTS.CLIENT.CASES.byId(caseId))
  },

  // Get client deadlines (optionally only upcoming)
  async getDeadlines(params?: {
    upcomingOnly?: boolean
  }): Promise<ClientDeadlinesResponse> {
    const query = new URLSearchParams()
    if (params?.upcomingOnly) query.append('upcoming_only', 'true')
    const queryString = query.toString() ? `?${query.toString()}` : ''
    return api.get<ClientDeadlinesResponse>(`${API_ENDPOINTS.CLIENT.DEADLINES}${queryString}`)
  },

  // Get client documents for a case (or all cases)
  async getDocuments(params?: {
    caseId?: number
    limit?: number
    offset?: number
  }): Promise<ClientDocumentsResponse> {
    if (params?.caseId) {
      return api.get<ClientDocumentsResponse>(API_ENDPOINTS.CLIENT.CASES.documents(params.caseId))
    }
    // If no caseId, we could get all documents across cases (but backend may not support)
    // For now, we'll return empty
    return { documents: [] }
  },

  // Download a document (returns blob URL)
  async downloadDocument(documentId: number): Promise<string> {
    const response = await api.get<Blob>(API_ENDPOINTS.CLIENT.DOCUMENTS.download(documentId), {
      responseType: 'blob',
    })
    const url = window.URL.createObjectURL(new Blob([response]))
    return url
  },

  // Upload a document for a case
  async uploadDocument(caseId: number, file: File, description?: string): Promise<ClientDocument> {
    const formData = new FormData()
    formData.append('file', file)
    if (description) {
      formData.append('description', description)
    }
    // Note: backend expects multipart/form-data
    return api.post<ClientDocument>(`${API_ENDPOINTS.CLIENT.DOCUMENTS.UPLOAD}?case_id=${caseId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  // Get case timeline events
  async getTimelineEvents(caseId: number, limit?: number): Promise<TimelineEvent[]> {
    const query = new URLSearchParams()
    if (limit) query.append('limit', limit.toString())
    const queryString = query.toString() ? `?${query.toString()}` : ''
    return api.get<TimelineEvent[]>(`${API_ENDPOINTS.CLIENT.CASES.timeline(caseId)}${queryString}`)
  },
}